import { prokaryote_labels } from "./pieChart.js";
/**
 * Parse a Newick string into a JS object.
 *
 * Returns an object of the form:
 * {
 *   name: <string or undefined>,
 *   branch_length: <number or undefined>,
 *   branchset: [<childNode>, <childNode>, ...] or undefined if leaf
 * }
 */
const TREE_FORMAT_WIDTHS = [50, 30, 15, 50, 30];
export function parseNewick(newick) {
  if (newick.endsWith(";")) newick = newick.slice(0, -1);
  const ancestors = [];
  let tree = {},
    current = tree,
    lastToken = null;

  // include single-quote in the “other” tokens
  const tokens = newick.match(/'[^']*'|[^():,;]+|[():,;]/g);
  for (let token of tokens) {
    switch (token) {
      case "(":
        if (!current.branchset) current.branchset = [];
        ancestors.push(current);
        const child = {};
        current.branchset.push(child);
        current = child;
        break;
      case ",":
        current = ancestors[ancestors.length - 1];
        const sib = {};
        current.branchset.push(sib);
        current = sib;
        break;
      case ")":
        current = ancestors.pop();
        break;
      case ":":
        break;
      default:
        // strip surrounding quotes if present
        if (lastToken === ":") {
          current.branch_length = parseFloat(token);
        } else {
          if (/^'.*'$/.test(token)) {
            // unescape doubled quotes inside name
            token = token.slice(1, -1).replace(/''/g, "'");
          }
          current.name = token;
        }
    }
    lastToken = token;
  }
  return tree;
}

export function needsQuoting(s) {
  // Whitespace, Newick punctuation, and braces, which phylotree's parser treats as
  // annotation delimiters when unquoted.
  return /[\s:;,()\[\]'{}]/.test(s);
}

export function escapeName(s) {
  // double any single-quotes in the name
  return s.replace(/'/g, "''");
}

/**
 * Convert a parsed tree object back into a Newick string.
 */
export function unparseNewick(node) {
  // internal
  if (node.branchset && node.branchset.length) {
    const kids = node.branchset.map(unparseNewick).join(",");
    let out = `(${kids})`;
    if (node.name) {
      let nm = node.name;
      if (needsQuoting(nm)) nm = `'${escapeName(nm)}'`;
      out += nm;
    }
    if (node.branch_length != null) out += `:${node.branch_length}`;
    return out;
  }
  // leaf
  let nm = node.name || "";
  if (needsQuoting(nm)) nm = `'${escapeName(nm)}'`;
  if (node.branch_length != null) nm += `:${node.branch_length}`;
  return nm;
}

/**
 * Tag the backend puts on query leaves. `pylotree.build_tree` names a query leaf
 * `identity-<key>`, where <key> is either a genomeID or the internal upload filename,
 * and that key is exactly how the identity entry is keyed in the frontend mapping.
 */
export const IDENTITY_PREFIX = "identity-";

/**
 * Display label for one query leaf.
 *
 * Prefers the filename the user actually uploaded. `filename` is the internal upload
 * name (e.g. "name-05ee83f1....sig"), which is only a fallback for identities
 * collected before originalFilename was recorded.
 *
 * @param {object|undefined} entry - identity entry from the mapping
 * @param {string} key - the leaf key, with the identity- tag already stripped
 * @returns {string} label to show on the tree
 */
export function identityLabel(entry, key) {
  if (entry && typeof entry === "object") {
    return entry.originalFilename || entry.filename || key;
  }
  return key;
}

/**
 * Marker the renderer looks for on a query leaf's display name. The marker travels IN
 * the name itself, so it survives everything a side-channel label set did not: the
 * cached `treeData`, toggleTreeData's re-parse, radial/linear re-renders, quoting
 * round-trips through unparseNewick, and the .nwk download (where naming the queries
 * outright is genuinely informative).
 */
export const QUERY_MARKER = "Query: ";

/**
 * Rename query leaves in place, and record the labels handed out.
 *
 * These leaves are skipped by renameLeaves/addMetadata: those look up `mapping[node.name]`,
 * and the name still carries the identity- tag, so it never matches.
 *
 * Each leaf becomes "Query: <label>", which is both the requested identifier (the
 * upload's own filename, unmistakably marked) and the signal the colorizer keys on.
 * A duplicate label, two uploads of one file, gets the short key appended, because
 * two leaves with one name cannot be told apart in a tree.
 *
 * @param {object} node - parsed newick node
 * @param {object} mapping - identity/genome entries keyed by bare key
 * @param {Set<string>|null} identityLabels - collects assigned labels; pass null to skip
 * @param {Set<string>} seen - labels already assigned in this walk
 */
export function renameIdentityLeaves(node, mapping, identityLabels = null, seen = new Set()) {
  if (node.branchset) {
    node.branchset.forEach((child) =>
      renameIdentityLeaves(child, mapping, identityLabels, seen),
    );
  }
  if (!node.name || !node.name.startsWith(IDENTITY_PREFIX)) return;

  const key = node.name.slice(IDENTITY_PREFIX.length);
  let label = QUERY_MARKER + identityLabel(mapping?.[key], key);
  if (seen.has(label)) label = `${label} (${key.slice(0, 8)})`;
  seen.add(label);
  node.name = label;
  if (identityLabels) identityLabels.add(label);
}

/**
 * Parse a genome's Attributes_JSON once and memoize the result on the object.
 *
 * Every chart processor and both tree walks used to re-parse the same JSON string per
 * genome per render. The memo is a non-enumerable property, because these objects are
 * serialised into the localStorage `Analysis` document and the parsed copy must never
 * ride along. Handles the string, already-an-object and absent forms; a malformed
 * string yields {} rather than a throw.
 */
export function attrsOf(genome) {
  if (!genome || typeof genome !== "object") return {};
  if (genome.__attrs) return genome.__attrs;
  const raw = genome.Attributes_JSON;
  let attrs = {};
  if (typeof raw === "string" && raw) {
    try {
      attrs = JSON.parse(raw) || {};
    } catch (err) {
      attrs = {};
    }
  } else if (raw && typeof raw === "object") {
    attrs = raw;
  }
  try {
    Object.defineProperty(genome, "__attrs", { value: attrs, configurable: true });
  } catch (err) {
    /* a frozen object just goes unmemoized */
  }
  return attrs;
}

/**
 * Recursively rename leaves in the parsed tree, if they match our genome mapping.
 *
 * The display name falls through NCBI organism name (with its strain), Strain, NCBI
 * AssemblyID, NCBI Accession Number, and finally keeps the genomeID the leaf arrived
 * with, which the table's metadata list can cross-reference. `seen` keeps the walk's
 * names unique: a repeated name gets the genomeID appended, because two leaves with
 * one label cannot be told apart in a tree.
 */
export function renameLeaves(node, mapping, seen = new Set()) {
  if (node.branchset) {
    // Internal node: recurse on children
    node.branchset.forEach((child) => renameLeaves(child, mapping, seen));
  }
  // Leaf node
  if (node.name && mapping[node.name]) {
    const gid = node.name;
    const attributesJson = attrsOf(mapping[gid]);
    const organism_name = attributesJson["NCBI organism name"];
    const strain = attributesJson["Strain"];

    let newName = null;
    if (organism_name) {
      newName = strain ? `${organism_name} st. ${strain}` : `${organism_name}`;
    } else {
      const fallback =
        strain ||
        attributesJson["NCBI AssemblyID"] ||
        attributesJson["NCBI Accession Number"];
      if (fallback) newName = String(fallback);
    }

    if (newName) {
      if (seen.has(newName)) newName = `${newName} (${gid})`;
      seen.add(newName);
      node.name = newName;
    }
  }
}

/**
 * Add metadata to the parsed tree.
 */
export function addMetadata(node, mapping) {
  // 1) Recurse into internal nodes
  if (node.branchset) {
    node.branchset.forEach((child) => addMetadata(child, mapping));
  }

  // 2) Only proceed if this leaf name matches a mapping entry. The mapping here is
  // structureTreeLeafMapping's output, genomeID -> preformatted row string, so the
  // rename is a straight substitution (the old Attributes_JSON parse in this function
  // was dead code: these values are strings, not genome objects).
  const rawName = node.name;
  if (!rawName || !mapping[rawName]) return;
  node.name = `${mapping[rawName]}`;
}

/**
 * Drop leaves from a Newick string and collapse what that leaves behind.
 *
 * Used by the analysis page to exclude unchecked genomes from an already-computed
 * tree without re-running the phylogeny job: the displayed tree becomes the induced
 * subtree of the checked set (topology and branch lengths still come from the full
 * computation; regenerating gives a true phylogeny of the subset). An internal node
 * left with one child is absorbed into that child, branch lengths summed, so no
 * degenerate unary chains render.
 *
 * @param {string} newickString - tree with its RAW leaf names (genomeID / identity-<key>)
 * @param {(name: string) => boolean} shouldDrop - true to remove that leaf
 * @returns {string|null} the pruned Newick, or null when nothing survives
 */
export function pruneLeaves(newickString, shouldDrop) {
  const root = parseNewick(newickString);
  function prune(node) {
    if (!node.branchset || !node.branchset.length) {
      return node.name && shouldDrop(node.name) ? null : node;
    }
    const kids = node.branchset.map(prune).filter(Boolean);
    if (kids.length === 0) return null;
    if (kids.length === 1) {
      const child = kids[0];
      if (node.branch_length != null || child.branch_length != null) {
        child.branch_length =
          (Number(node.branch_length) || 0) + (Number(child.branch_length) || 0);
      }
      return child;
    }
    node.branchset = kids;
    return node;
  }
  const pruned = prune(root);
  return pruned ? unparseNewick(pruned) + ";" : null;
}

/**
 * Main function to replace leaf names in a Newick string using the mapping.
 */
export function replaceLeafNames(newickString, genomeMapping) {
  const treeObj = parseNewick(newickString);
  // JSON round-trip rather than structuredClone: the parsed-Newick tree is plain
  // JSON-safe data, and structuredClone is missing in Safari <15.4 / Chrome <98.
  const metadataTree = JSON.parse(JSON.stringify(treeObj));
  const identityLabels = new Set();

  // Reference leaves first: both passes below match on node.name, and renameLeaves only
  // recognises bare keys, so query leaves have to keep their identity- tag until it runs.
  renameLeaves(treeObj, genomeMapping);
  addMetadata(metadataTree, structureTreeLeafMapping(genomeMapping));

  // Then the query leaves, in both trees, so the label is the same whichever view is shown.
  renameIdentityLeaves(treeObj, genomeMapping, identityLabels);
  renameIdentityLeaves(metadataTree, genomeMapping);

  return [
    unparseNewick(treeObj) + ";",
    unparseNewick(metadataTree) + ";",
    identityLabels,
  ];
}

/**
 *
 * @param {array} cols array of column values
 * @param {array} widths array of column widths
 * @returns {string} formatted row string
 */
export function formatRow(cols, widths) {
  return cols.map((c, i) => String(c).padEnd(widths[i])).join(" ");
}

/**
 * Structure tree leaf mapping to gid: string format for phylogeny display
 * @param {dictionary} genomeMapping e.g. genomeID: { Attributes_JSON: { "NCBI organism name": "OrganismA", "Strain": "StrainX" }, genomeID": "3024", "Scaffolds": "57", "Length": "5241139","addedAt": "2022-11-16 05:46:13", "dlin": "{\"0\": 76, \"1\": 6, \"2\": 13}",, lin [...] },...
 * @returns {dictionary} structured mapping for tree leaves genomeID: organism_name str. strain date isolation_source_category country
 * @param {number} cutoffLength maximum number of labels to show (negative means show all)
 */
export function structureTreeLeafMapping(genomeMapping, cutoffCategory = -1) {
  const structuredMapping = {};
  // column widths for formatting the value string
  for (const [gid, genome_attributes] of Object.entries(genomeMapping)) {
    const attributesJson = attrsOf(genome_attributes);
    const organism_name = attributesJson["NCBI organism name"] ?? "N/A";
    // A real emptiness check: the old comparison against the string "undefined" let an
    // absent Strain render as "str. undefined" in the metadata view.
    const strainVal = attributesJson["Strain"];
    const strain =
      strainVal != null && strainVal !== "" && strainVal !== "undefined"
        ? `str. ${strainVal}`
        : "N/A";
    const date = attributesJson["Date of Isolation"] ?? "N/A";
    let isoC = attributesJson["Isolation Source Category"] ?? "-1";
    if (prokaryote_labels && prokaryote_labels[parseInt(isoC)])
      isoC = prokaryote_labels[parseInt(isoC)].label;
    else isoC = "N/A";
    const country = attributesJson["Country"] ?? "N/A";
    let cutoffLength = TREE_FORMAT_WIDTHS.reduce((acc, val) => acc + val, 0);
    if (cutoffCategory > 0 && cutoffCategory < TREE_FORMAT_WIDTHS.length) {
      cutoffLength = TREE_FORMAT_WIDTHS.slice(0, cutoffCategory + 1).reduce(
        (a, b) => a + b,
        0,
      );
    }
    structuredMapping[gid] = formatRow(
      [organism_name, strain, date, isoC, country],
      TREE_FORMAT_WIDTHS,
    ).slice(0, cutoffLength);
  }
  return structuredMapping;
}

/**
 * Apply the structured leaf mapping to the displayed tree.
 * @param {object} tree phylotree object
 * @param {dictionary} genomeMapping structured mapping for tree leaves genomeID: organism_name str. strain date isolation_source_category country
 * @param {number} cutoffLength maximum number of labels to show (negative means show all)
 */
export function applyTreeLeafMapping(tree, genomeMapping, cutoffCategory = -1) {
  if (cutoffCategory < 0 || cutoffCategory >= TREE_FORMAT_WIDTHS.length) {
    cutoffCategory = TREE_FORMAT_WIDTHS.length - 1;
  }
  const cutoffLength = TREE_FORMAT_WIDTHS.slice(0, cutoffCategory + 1).reduce(
    (a, b) => a + b,
    0,
  );
  tree.display.svg
    .selectAll("text.phylotree-node-text")
    .each(function (data, index) {
      if (this && this.parentElement?.dataset?.nodeName !== "undefined") {
        // guard if d is undefined
        const gid = !isNaN(this.parentElement.dataset.nodeID)
          ? parseInt(this.parentElement.dataset?.nodeID)
          : parseInt(this.parentElement.dataset.nodeName);
        this.parentElement.dataset.nodeID = `${gid}`; // ensure nodeID is set for future reference
        this.style.whiteSpace = "pre-wrap"; // preserve spaces for alignment
        this.style.fontFamily = "monospace"; // use monospace font for alignment
        this.textContent =
          genomeMapping?.[gid].substr(0, cutoffLength) ?? `${gid}` ?? "";
        this.parentElement.dataset.nodeName = this.textContent; // update nodeName dataset for future reference
      }
    });

  tree.display.show();
  return tree;
}

// const treeString = `(((191608:0.01322,(102666:0.00612,102655:0.00519)Inner6:0.00826)Inner7:0.26873,(102668:0.00587,5626:0.00606)Inner3:0.27998)Inner8:0.09409,((232870:0.00894,14103:0.00872)Inner2:0.40306,((102886:0.00455,5654:0.00447)Inner1:0.48345,5653:0.50027)Inner9:0.07883)Inner10:0.05066,(199647:0.01074,(102673:0.00668,102670:0.00775)Inner4:0.00253)Inner5:0.33613)Inner11:0.00000;`;

// // Example mapping (genomeId -> organism_name, strain)
// const genomeMapping = {
//     191608: { Attributes_JSON: { "NCBI organism name": "OrganismA", "Strain": "StrainX" } },
//     102666: { Attributes_JSON: { "NCBI organism name": "OrganismB", "Strain": "StrainY" } },
//     102655: { Attributes_JSON: { "NCBI organism name": "OrganismC", "Strain": "StrainZ" } },
//     102668: { Attributes_JSON: { "NCBI organism name": "OrganismD", "Strain": "StrainW" } },
//     5626:   { Attributes_JSON: { "NCBI organism name": "OrganismE", "Strain": "StrainV" } },
//     232870: { Attributes_JSON: { "NCBI organism name": "OrganismF", "Strain": "StrainU" } },
//     14103:  { Attributes_JSON: { "NCBI organism name": "OrganismG", "Strain": "StrainT" } },
//     102886: { Attributes_JSON: { "NCBI organism name": "OrganismH", "Strain": "StrainS" } },
//     5654:   { Attributes_JSON: { "NCBI organism name": "OrganismI", "Strain": "StrainR" } },
//     5653:   { Attributes_JSON: { "NCBI organism name": "OrganismJ", "Strain": "StrainQ" } },
//     199647: { Attributes_JSON: { "NCBI organism name": "OrganismK", "Strain": "StrainP" } },
//     102673: { Attributes_JSON: { "NCBI organism name": "OrganismL", "Strain": "StrainO" } },
//     102670: { Attributes_JSON: { "NCBI organism name": "OrganismM", "Strain": "StrainN" } }
// };

// // Replace leaves and log the newick
// const replacedTree = replaceLeafNames(treeString, genomeMapping);
// console.log(replacedTree);
